'use client';

import React from 'react';
import GeneralSection from '../GeneralSection';
import Tabs from './Tabs';
import PositionOverview from './position-overview';
import DevelopmentPath from './development-path';
import ResumeFocus from './resume-focus';
import SalaryInsight from './salary-insight';
import RecruitmentTrend from './recruitment-trend';
import HotJobOpportunity from './recruitment-trend/HotJobOpportunity';
import RecommendPost from '../recommend-post';
import { getJobTitlePagePosts } from '@/utils/articles';
import { deduplicateObjListByKey } from '@/utils/common-utils';
import HighSalaryRank from '../high-salary-rank';
import LinksContainer from '@/components/seo/LinksContainer';
import { JobTitleContext } from '@/context/create-context';
import { SeoHotLinksType } from '@/types/common';
import { ACTIVE_TABS } from '@/constants/job-title';
import {
  JobTitleType,
  JobTitleSummaryType,
  SimilarJobTitleType,
} from '@/types/job-title';

const JobTitle = (props: {
  jobTitle: JobTitleType;
  jobTitleSummary: JobTitleSummaryType;
  similarJobTitles: SimilarJobTitleType[];
  seoHotLinks: SeoHotLinksType;
  isJobTitleCity?: boolean;
}) => {
  const {
    jobTitle,
    jobTitleSummary,
    seoHotLinks,
    similarJobTitles,
    isJobTitleCity,
  } = props;
  const [selectedTab, setSelectedTab] = React.useState(
    isJobTitleCity ? ACTIVE_TABS[3].en : ACTIVE_TABS[0].en,
  );
  const displayName = `${isJobTitleCity ? jobTitle.city_name || '' : ''}${jobTitle.display_name}`;

  const seoDetail = React.useMemo(() => {
    const seoContent = jobTitle.job_open_details.seo_content || {};
    const name = jobTitle.name;
    const displayName = jobTitle.display_name;
    if (name === displayName) {
      return seoContent;
    }
    const replaceNameInSeoContent = (value: unknown): unknown => {
      if (typeof value === 'string') {
        return value.replaceAll(name, displayName);
      }
      if (Array.isArray(value)) {
        return value.map((item) => replaceNameInSeoContent(item));
      }
      if (value && typeof value === 'object') {
        return Object.fromEntries(
          Object.entries(value).map(([key, item]) => [
            key,
            replaceNameInSeoContent(item),
          ]),
        );
      }
      return value;
    };
    return replaceNameInSeoContent(seoContent) as typeof seoContent;
  }, []);

  const aliases = React.useMemo(() => {
    const regx = /初级|中级|高级|资深/gi;
    const filtered = jobTitle.aliases
      .filter((item) => item.name !== displayName)
      .map((item) => ({
        ...item,
        name: item.name.replace(regx, ''),
      }))
      .filter((item) => item.name !== displayName);
    return deduplicateObjListByKey(filtered, 'name');
  }, []);

  return (
    <JobTitleContext.Provider
      value={{
        jobTitle,
        jobTitleSummary,
        displayName,
        isJobTitleCity,
      }}
    >
      <Tabs selectedTab={selectedTab} setSelectedTab={setSelectedTab} />
      <GeneralSection
        marginTop="mt-8"
        rightComponent={
          <>
            <RecommendPost
              fetchPosts={() =>
                getJobTitlePagePosts({
                  name: jobTitle.name,
                  l1_l2_list: jobTitle.l1_l2_list,
                  size: 4,
                })
              }
            />
            <HighSalaryRank
              id={jobTitle.salary_rank.l1_id}
              name={jobTitle.salary_rank.l1_name}
              city={jobTitle.location?.pinyin}
            />
          </>
        }
      >
        {!isJobTitleCity && (
          <>
            <PositionOverview
              show={selectedTab === ACTIVE_TABS[0].en}
              jobData={seoDetail['岗位做什么的'] || {}}
              aiReplacementData={seoDetail['岗位会被取代'] || {}}
              marketData={seoDetail['行业方向选择'] || {}}
              fitData={seoDetail['适合这个岗位'] || {}}
              entryData={seoDetail['如何入行'] || {}}
            />
            <DevelopmentPath
              show={selectedTab === ACTIVE_TABS[1].en}
              pathData={seoDetail['职业成长路径'] || {}}
              stagData={seoDetail['你的职业阶段'] || {}}
              capabilityData={seoDetail['能力发展地图'] || {}}
            />
            <ResumeFocus
              show={selectedTab === ACTIVE_TABS[2].en}
              abilityData={seoDetail['核心能力'] || {}}
              achievementData={seoDetail['呈现工作成果'] || {}}
              resumeScreeningData={seoDetail['如何筛选简历'] || {}}
              standoutData={seoDetail['简历脱颖而出'] || {}}
              highlightData={seoDetail['加分亮点'] || {}}
              marketPreferenceData={seoDetail['市场偏爱特质'] || {}}
              avoidTrapsData={seoDetail['规避表达陷阱'] || {}}
            />
          </>
        )}
        <SalaryInsight
          show={selectedTab === ACTIVE_TABS[3].en}
          salaryData={seoDetail['平均月薪'] || {}}
          experienceData={seoDetail['工作年限'] || {}}
          educationData={seoDetail['学历背景'] || {}}
          industryData={seoDetail['所在行业'] || {}}
          cityData={seoDetail['所在城市'] || {}}
        />
        <RecruitmentTrend
          show={selectedTab === ACTIVE_TABS[4].en}
          recruitmentData={seoDetail['新增岗位'] || {}}
          experienceData={seoDetail['需求分析'] || {}}
          industryData={seoDetail['不同行业需求'] || {}}
          cityData={seoDetail['城市需求分析'] || {}}
        />
        {selectedTab !== ACTIVE_TABS[4].en && (
          <LinksContainer
            seoHotLinks={seoHotLinks}
            similarJobTitles={similarJobTitles}
            aliases={aliases}
            city={
              isJobTitleCity
                ? {
                    name: jobTitle.location?.name || '',
                    pinyin: jobTitle.location?.pinyin || '',
                  }
                : undefined
            }
          />
        )}
      </GeneralSection>
      <HotJobOpportunity show={selectedTab === ACTIVE_TABS[4].en} />
    </JobTitleContext.Provider>
  );
};

export default JobTitle;
